/**
 * Copyright 2024 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */
// Polyfill for Object.hasOwn
if (!Object.hasOwn) {
  Object.hasOwn = function(obj, prop) {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  };
}
import React, { useEffect, useState, useRef } from "react";
import "antd/dist/antd.min.css";
import { Context } from "./stores/context";
import { AppRoutes } from "./routes";
import { useLocation, useNavigationType } from "react-router-dom";
import { useNavigate } from 'src/hooks/useNavigate';
import { useDebounceFn } from "ahooks";
import { isInWechatMiniProgram } from "./utils";
import { useTranslation } from "react-i18next";
import Toast from "./services/toast.service"
import { api } from "src/services/api.service";
import { IUserInfo } from "./types/interfaces/api.interface"
import i18n from "./i18n"

/**
 * 不检查token及用户信息的页面
 */
const PublicRoutes = [
  "/login",
  "/emailLogin",
  "/oauth",
  "/userAgreement",
  "/privacyPolicy",
]

/**
 * 模块描述
 * @module App index
 */
const App: React.FC<Record<string, unknown>> = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [navStackLength, setNavStackLength] = useState(1);
  const [isMiniProgram, setIsMiniProgram] = useState(false);
  const [isMiniProgramInitialized, setIsMiniProgramInitialized] = useState(false);
  // 全局用户信息状态
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(null);
  const [isUserInfoLoading, setIsUserInfoLoading] = useState(false);

  const navigate = useNavigate();

  // 用于管理 deeplink 重试的定时器和 toast
  const deeplinkTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 清理 deeplink 相关资源的函数
  const clearDeeplinkResources = () => {
    if (deeplinkTimerRef.current) {
      clearTimeout(deeplinkTimerRef.current);
      deeplinkTimerRef.current = null;
    }
  };

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      clearDeeplinkResources();
    };
  }, []);

  // 防抖处理 deeplink 逻辑
  const { run: handleDeeplinkDebounced } = useDebounceFn((res: any) => {
    if (res.deeplink) {
      try {
        console.log("收到deeplink:", res.deeplink);

        // 解析deeplink URL
        const deeplinkUrl = new URL(res.deeplink);
        const searchParams = deeplinkUrl.searchParams;

        // 获取参数
        const isFromH5 = searchParams.get('isFromH5');
        const h5Path = searchParams.get('path');
        const h5Uid = searchParams.get('uid');

        console.log("Deeplink参数:", { isFromH5, h5Path, h5Uid });

        // 检查是否来自H5
        if (isFromH5 === 'true' && h5Path) {
          // 先清理之前可能存在的资源
          clearDeeplinkResources();

          // 延迟检查函数，支持重试机制
          const checkUidAndNavigate = (retryCount = 0) => {
            const currentUid = localStorage.getItem("uid");
            
            console.log("xcr currentUid", currentUid)

            // 如果没有 uid 且重试次数少于40次，则等待500ms后重试
            if (!currentUid && retryCount < 40) {
              console.log(`第${retryCount + 1}次检查，uid还未加载，等待500ms后重试...`);
              deeplinkTimerRef.current = setTimeout(() => {
                checkUidAndNavigate(retryCount + 1);
              }, 500);
              return;
            }

            console.log("UID检查:", { currentUid, h5Uid, retryCount });

            // UID一致性检查
            const canJump = currentUid === h5Uid || !h5Uid || !currentUid;

            if (canJump/* || h5Path === '/'*/) {
              console.log("UID检查通过，即将跳转到:", h5Path);
              // 使用相对路径进行路由跳转
              navigate(h5Path);
            } else {
              console.warn("UID不匹配，无法跳转", { currentUid, h5Uid });
              Toast.show({
                content: t("检测到登录账号不一致，请切换至对应账号以继续访问"),
                duration: 3000,
              });
            }
          };

          // 开始检查并处理
          checkUidAndNavigate();
        }
      } catch (error) {
        console.error("处理deeplink时出错:", error);
      }
    }
  }, { wait: 500 });

  // 检查token并获取用户信息
  const checkTokenAndGetUserInfo = async () => {
    const token = localStorage.getItem('token');
    const currentPath = location.pathname;
    
    // 如果是登录页或OAuth页面，不获取用户信息
    if (PublicRoutes.includes(currentPath)) {
      return;
    }
    
    // 如果token存在，则获取用户信息
    if (token) {
      try {
        setIsUserInfoLoading(true);
        const response = await api.getUserInfo({ skipAuth401: true });
        if (response.data) {
          setUserInfo(response.data);
        }
      } catch (error) {
        setUserInfo(null);
      } finally {
        setIsUserInfoLoading(false);
      }
    }
  };

  // 组件挂载时检查token并获取用户信息
  useEffect(() => {
    checkTokenAndGetUserInfo();
  }, []);

  // 初始化微信小程序环境检测
  useEffect(() => {
    const initMiniProgramStatus = async () => {
      try {
        const isMini = await isInWechatMiniProgram();
        setIsMiniProgram(isMini);
      } catch (error) {
        console.error('检测微信小程序环境失败:', error);
        setIsMiniProgram(false);
      } finally {
        setIsMiniProgramInitialized(true);
      }
    };
    setTimeout(() => {
      initMiniProgramStatus();
    }, 10);
  });

  // 监听路由变化，更新navStackLength，用于NavBar组件的显示
  useEffect(() => {
    setNavStackLength((prev) => {
      switch (navigationType) {
        case 'PUSH':
          return prev + 1;
        case 'POP':
          return Math.max(1, prev - 1); // 确保最小值1
        case 'REPLACE':
        default:
          return prev;
      }
    });
  }, [location.key, navigationType]);

  return (
    <Context.Provider
      value={{
        navStackLength,
        isMiniProgram,
        setIsMiniProgram,
        isMiniProgramInitialized,
        setIsMiniProgramInitialized,
        // 全局用户信息
        userInfo,
        setUserInfo,
        isUserInfoLoading,
        setIsUserInfoLoading,
      }}
    >
      <AppRoutes />
    </Context.Provider>
  );
};

export default App;
